import { TooltipDirective } from '../../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../../componentes/botones';
import {
  Component,
  effect,
  inject,
  input,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { IEventoGenerico, IFilter, IQueryParam } from 'modelos/src';
import {
  GraficosComponent,
  IBarChartData,
} from '../../../../auxiliares/graficos/graficos.component';
import { EventosGenericosHttpService } from '../../../../auxiliares/servicios/http/eventos-genericos.service';
import { ChartColors } from '../../../../auxiliares/servicios/chart-colors.service';

/**
 * Gráfico reutilizable "Eventos por tipo de alarma" para los dashboards de
 * luminarias (general, puesta, grupo de luminarias, grupos de puestas y detalle
 * individual). Agrupa los `IEventoGenerico` de `tipoEvento === 'Evento Luminaria'`
 * por `valores.titulo` (el código de alarma) y los grafica como barras
 * horizontales ordenadas de mayor a menor.
 *
 * Modos de filtrado:
 *  - `idsLuminarias`: filtra por `idEntidad ∈ idsLuminarias` (agrupación/detalle).
 *  - `idCliente`: filtra por cliente (dashboard general). Se ignora si hay
 *    `idsLuminarias`.
 */
@Component({
  selector: 'app-grafico-eventos-luminarias',
  standalone: true,
  imports: [TooltipDirective, 
    CommonModule,
    MatIconModule,
    BOTONES,
    
    MatProgressSpinnerModule,
    GraficosComponent,
  ],
  templateUrl: './grafico-eventos-luminarias.component.html',
  styleUrl: './grafico-eventos-luminarias.component.scss',
})
export class GraficoEventosLuminariasComponent {
  private eventosService = inject(EventosGenericosHttpService);

  // Inputs
  public titulo = input<string>('Eventos por tipo de alarma');
  public idCliente = input<string>();
  public idsLuminarias = input<string[]>();
  public altura = input<string>('340px');
  // true → renderiza su propia card (uso suelto, ej. detalle). false → sin
  // marco, para vivir dentro de la card del grid de gráficos del dashboard.
  public card = input<boolean>(true);
  // false → oculta el título interno del gráfico (cuando el título lo aporta
  // la card contenedora, ej. app-content-card en el detalle de luminaria).
  public mostrarTitulo = input<boolean>(true);

  // Estado
  public series = signal<IBarChartData[]>([]);
  public categorias = signal<string[]>([]);
  public cargando = signal<boolean>(true);
  public total = signal<number>(0);
  public hayDatos = computed(() => this.total() > 0);
  // false → solo eventos abiertos (default). true → histórico completo (incluye
  // finalizados). Se alterna con el botón del encabezado.
  public mostrarHistorico = signal<boolean>(false);

  public toggleHistorico(): void {
    this.mostrarHistorico.update((v) => !v);
  }

  constructor() {
    // Re-consulta cuando cambian los inputs de filtrado (las luminarias de los
    // dashboards de agrupación llegan de forma asíncrona) o el modo histórico.
    effect(() => {
      const ids = this.idsLuminarias();
      const cli = this.idCliente();
      const historico = this.mostrarHistorico();
      void this.cargar(ids, cli, historico);
    });
  }

  private async cargar(
    idsLuminarias: string[] | undefined,
    idCliente: string | undefined,
    mostrarHistorico: boolean,
  ): Promise<void> {
    this.cargando.set(true);

    try {
      // Sin luminarias en una agrupación → no hay nada que consultar.
      if (idsLuminarias && idsLuminarias.length === 0) {
        this.aplicarConteo([]);
        this.cargando.set(false);
        return;
      }

      const filter: IFilter<IEventoGenerico> = {
        tipoEvento: 'Evento Luminaria',
      };
      // Por defecto solo abiertos; en modo histórico no se filtra por estado.
      if (!mostrarHistorico) {
        (filter as Record<string, unknown>)['estado'] = { $ne: 'Finalizada' };
      }
      if (idsLuminarias && idsLuminarias.length) {
        (filter as Record<string, unknown>)['idEntidad'] = {
          $in: idsLuminarias,
        };
      } else if (idCliente) {
        filter.idCliente = idCliente;
      }

      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        select: 'valores.titulo',
        includeChildren: true,
        unlimited: true,
      };

      const data = await this.eventosService.getFiltered(query);
      const conteo = new Map<string, number>();
      for (const ev of data.datos) {
        const valores = ev?.valores as { titulo?: string } | undefined;
        const titulo = valores?.titulo?.trim() || 'Sin título';
        conteo.set(titulo, (conteo.get(titulo) ?? 0) + 1);
      }
      this.aplicarConteo([...conteo.entries()]);
    } catch (error) {
      console.error('Error cargando eventos de luminaria:', error);
      this.aplicarConteo([]);
    } finally {
      this.cargando.set(false);
    }
  }

  // Ordena de mayor a menor y arma series + categorías para el gráfico de barras.
  private aplicarConteo(entradas: [string, number][]): void {
    const ordenadas = [...entradas].sort((a, b) => b[1] - a[1]);
    const categorias = ordenadas.map(([titulo]) => titulo);
    const valores = ordenadas.map(([, cantidad]) => cantidad);
    const colores = categorias.map((_, i) =>
      ChartColors.getCategoricalByIndex(i),
    );

    this.categorias.set(categorias);
    this.series.set([{ name: 'Eventos', data: valores, colors: colores }]);
    this.total.set(valores.reduce((acc, v) => acc + v, 0));
  }
}
